const appT = window.CNSI18n?.t || ((text, values = {}) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? ''));
const appRoot = document.querySelector('#cns-app') || document;
const views = {
  explore: {name:'Side by side', description:'Keep your finds in view. Product details open alongside the collection, so you can explore without switching back and forth.', alt:'CHINASQUAD macOS 1.0.5 screenshot: product grid with a full-height product details panel'},
  casa: {name:'Casa AI', description:'Inspiration starts with a conversation. Ask Casa about shopping, styling or your orders.', alt:'CHINASQUAD macOS 1.0.5 screenshot: product grid and Casa welcome screen'},
  squad: {name:'SQUAD inspiration', description:'Find inspiration in the community. Browse SQUAD and meet the styles and creators that speak to you.', alt:'CHINASQUAD macOS 1.0.5 screenshot: SQUAD community feed'},
  tools: {name:'Style tools', description:'Your closet, creative workspace and everyday tools, together in Tools. Put your next idea into motion.', alt:'CHINASQUAD macOS 1.0.5 screenshot: Tools'}
};
const tabs = [...appRoot.querySelectorAll('[role="tab"][data-mode]')];
const screenshot = appRoot.querySelector('#app-screenshot');
const mobileScreenshot = appRoot.querySelector('#mobile-screenshot');
const mobilePreview = appRoot.querySelector('#mobile-preview');
const mobileCaption = appRoot.querySelector('#mobile-preview-caption');
const featurePanel = appRoot.querySelector('#feature-preview');
const mobileViews = {
  explore: {name:'Product details', alt:'Official CHINASQUAD iPhone App Store image showing product details'},
  casa: {name:'Casa AI', alt:'CHINASQUAD mobile app screenshot showing the Casa AI welcome screen', source:'Actual iPhone Duo simulator screenshot', note:'Captured from the running CHINASQUAD development build in the iPhone Duo simulator, in its unfolded portrait layout.'},
  squad: {name:'SQUAD inspiration', alt:'Official CHINASQUAD iPhone App Store image showing the SQUAD community feed'},
  tools: {name:'Your closet', alt:'Official CHINASQUAD iPhone App Store image showing the closet, Studio and Lookbook'}
};
for (const view of [...Object.values(views), ...Object.values(mobileViews)]) {
  for (const key of Object.keys(view)) view[key] = appT(view[key]);
}
let currentMode = 'explore';
let imageRequest = 0;
let imageAnimations = [];
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
function loadPreview(source) {
  const image = new Image();
  image.src = source;
  return image.decode().then(() => image);
}
async function setFeature(mode, focus = false) {
  const view = views[mode];
  if (!view) return;
  const activeTab = appRoot.querySelector('#tab-' + mode);
  if (focus) activeTab.focus();
  const request = ++imageRequest;
  if (mode === currentMode) {
    featurePanel.removeAttribute('aria-busy');
    return;
  }
  featurePanel.setAttribute('aria-busy', 'true');
  let images;
  try {
    images = await Promise.all([loadPreview(activeTab.dataset.previewSrc), loadPreview(activeTab.dataset.mobilePreviewSrc)]);
  } catch {
    if (request === imageRequest) featurePanel.removeAttribute('aria-busy');
    return;
  }
  if (request !== imageRequest) return;
  currentMode = mode;
  tabs.forEach(tab => {
    const selected = tab.dataset.mode === mode;
    tab.setAttribute('aria-selected', String(selected));
    tab.tabIndex = selected ? 0 : -1;
  });
  featurePanel.setAttribute('aria-labelledby', activeTab.id);
  featurePanel.removeAttribute('aria-busy');
  appRoot.querySelector('#preview-description').textContent = view.description;
  imageAnimations.forEach(animation => animation.cancel());
  screenshot.src = images[0].src;
  screenshot.alt = view.alt;
  mobileScreenshot.src = images[1].src;
  mobileScreenshot.alt = mobileViews[mode].alt;
  mobileScreenshot.width = images[1].naturalWidth;
  mobileScreenshot.height = images[1].naturalHeight;
  const mobileSource = mobileViews[mode].source || appT('Official App Store image');
  mobileCaption.textContent = mobileViews[mode].name + ' · ' + mobileSource;
  mobilePreview.dataset.previewTitle = mobileViews[mode].name + ' · ' + appT('iPhone app');
  mobilePreview.dataset.previewNote = mobileViews[mode].note || appT('Official promotional image from the CHINASQUAD App Store listing.');
  mobilePreview.setAttribute('aria-label', appT('Enlarge the mobile {name} preview', {name: mobileViews[mode].name}));
  imageAnimations = reduceMotion.matches ? [] : [screenshot, mobileScreenshot].map(image =>
    image.animate([{opacity:.55},{opacity:1}], {duration:280,easing:'ease-out'}));
}
for (const tab of tabs) {
  [tab.dataset.previewSrc, tab.dataset.mobilePreviewSrc].forEach(source => loadPreview(source).catch(() => {}));
}
tabs.forEach((tab, index) => {
  tab.addEventListener('click', () => setFeature(tab.dataset.mode));
  tab.addEventListener('keydown', event => {
    let next = index;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault(); setFeature(tabs[next].dataset.mode, true);
  });
});
appRoot.querySelectorAll('[data-feature-link]').forEach(link => link.addEventListener('click', () => setFeature(link.dataset.featureLink)));
const previewDialog = appRoot.querySelector('#preview-dialog');
const previewTrigger = appRoot.querySelector('[data-open-preview]');
let activePreviewTrigger = previewTrigger;
function showScreenshot(trigger, img, title, note) {
  activePreviewTrigger = trigger;
  const previewImage = appRoot.querySelector('#preview-full-image');
  previewImage.src = img.src;
  previewImage.alt = img.alt;
  previewImage.width = Number(img.getAttribute('width'));
  previewImage.height = Number(img.getAttribute('height'));
  previewDialog.classList.toggle('portrait-preview', previewImage.width < previewImage.height);
  appRoot.querySelector('#preview-dialog-title').textContent = title;
  previewDialog.querySelector(':scope > p').textContent = note;
  previewDialog.showModal();
  document.body.style.overflow = 'hidden';
}
previewTrigger.addEventListener('click', () => showScreenshot(previewTrigger, screenshot,
  views[currentMode].name + ' · ' + appT('Actual macOS screenshot'), appT('Captured from the running CHINASQUAD macOS 1.0.5 app.')));
appRoot.querySelectorAll('[data-preview-image]').forEach(trigger => trigger.addEventListener('click', () =>
  showScreenshot(trigger, trigger.querySelector('img'), trigger.dataset.previewTitle, trigger.dataset.previewNote)));
previewDialog.querySelector('.preview-close').addEventListener('click', () => previewDialog.close());
previewDialog.addEventListener('click', event => {
  if (event.target !== previewDialog) return;
  const rect = previewDialog.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) previewDialog.close();
});
previewDialog.addEventListener('close', () => { document.body.style.overflow = ''; activePreviewTrigger?.focus(); });

const pickers = [...appRoot.querySelectorAll('.platform-picker')];
const hoverTimers = new WeakMap();
function closePicker(picker, restoreFocus = false) {
  const button = picker.querySelector('button');
  picker.dataset.pinned = 'false';
  button.setAttribute('aria-expanded', 'false');
  picker.querySelector('.platform-menu').hidden = true;
  if (restoreFocus) button.focus();
}
function openPicker(picker) {
  clearTimeout(hoverTimers.get(picker));
  pickers.forEach(other => { if (other !== picker) closePicker(other); });
  picker.querySelector('button').setAttribute('aria-expanded', 'true');
  picker.querySelector('.platform-menu').hidden = false;
}
pickers.forEach(picker => {
  const button = picker.querySelector('button');
  button.addEventListener('click', event => {
    if (picker.dataset.pinned === 'true') closePicker(picker);
    else { openPicker(picker); picker.dataset.pinned = 'true'; }
  });
  picker.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') openPicker(picker); });
  picker.addEventListener('pointerleave', () => {
    hoverTimers.set(picker, setTimeout(() => {
      if (!picker.contains(document.activeElement)) closePicker(picker);
    }, 180));
  });
  picker.addEventListener('focusout', () => setTimeout(() => {
    if (!picker.contains(document.activeElement) && !picker.matches(':hover')) closePicker(picker);
  }, 0));
  picker.addEventListener('keydown', event => {
    if (event.key === 'Escape') { event.preventDefault(); closePicker(picker, true); }
    if (event.key === 'ArrowDown' && event.target === button) {
      event.preventDefault(); openPicker(picker); picker.querySelector('.platform-menu a')?.focus();
    }
  });
});
document.addEventListener('click', event => {
  pickers.forEach(picker => { if (!picker.contains(event.target)) closePicker(picker); });
});

const navToggle = appRoot.querySelector('.mobile-toggle');
const nav = appRoot.querySelector('#site-nav');
function closeNav() { navToggle.setAttribute('aria-expanded', 'false'); nav.classList.remove('nav-open'); navToggle.setAttribute('aria-label', appT('Open navigation')); }
navToggle?.addEventListener('click', () => {
  const open = navToggle.getAttribute('aria-expanded') !== 'true';
  navToggle.setAttribute('aria-expanded', String(open));
  navToggle.setAttribute('aria-label', open ? appT('Close navigation') : appT('Open navigation'));
  nav.classList.toggle('nav-open', open);
});
nav?.querySelectorAll('a').forEach(link => link.addEventListener('click', closeNav));
document.addEventListener('keydown', event => { if (event.key === 'Escape' && nav?.classList.contains('nav-open')) { closeNav(); navToggle.focus(); } });

const releaseDialog = appRoot.querySelector('#release-dialog');
let dialogTrigger;
appRoot.querySelectorAll('[data-open-release]').forEach(button => button.addEventListener('click', () => {
  dialogTrigger = button;
  releaseDialog.showModal();
  document.body.style.overflow = 'hidden';
}));
releaseDialog.querySelector('.dialog-close').addEventListener('click', () => releaseDialog.close());
releaseDialog.addEventListener('click', event => {
  if (event.target !== releaseDialog) return;
  const rect = releaseDialog.getBoundingClientRect();
  if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) releaseDialog.close();
});
releaseDialog.addEventListener('close', () => { document.body.style.overflow = ''; dialogTrigger?.focus(); });
const toast = appRoot.querySelector('#download-toast');
let toastTimer;
appRoot.querySelectorAll('a[download], a[data-download-platform]').forEach(link => link.addEventListener('click', () => {
  pickers.forEach(picker => closePicker(picker));
  if (releaseDialog.open) releaseDialog.close();
  const isWindows = link.dataset.downloadPlatform === 'windows';
  toast.querySelector('span').textContent = isWindows
    ? (link.dataset.downloadKind === 'test-kit'
      ? appT('Extract the test-kit ZIP, then run Portable/Run-Native-Smoke.cmd. The native window saves a screenshot and report, then closes.')
      : appT('Your Windows Native Alpha download has started. Open the .exe to install. macOS feature parity and Windows device testing are still in progress.'))
    : appT('Your download has started. Open the .dmg file and drag CHINASQUAD to Applications to install.');
  toast.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.hidden = true; }, 8500);
}));
toast.querySelector('button').addEventListener('click', () => { toast.hidden = true; clearTimeout(toastTimer); });
const copyrightYear = appRoot.querySelector('#copyright-year');
if (copyrightYear) copyrightYear.textContent = new Intl.DateTimeFormat('en', { year: 'numeric', timeZone: 'America/Los_Angeles' }).format(new Date());

appRoot.querySelectorAll('a[href="#windows-notes"]').forEach(link => link.addEventListener('click', () => {
  appRoot.querySelector('#windows-notes').open = true;
  pickers.forEach(picker => closePicker(picker));
  if (releaseDialog.open) releaseDialog.close();
}));
appRoot.querySelectorAll('.platform-menu a').forEach(link => link.addEventListener('click', () => pickers.forEach(picker => closePicker(picker))));
